import { useCallback, useEffect, useState } from 'react';
import type { CacheStoreApi, OptionalParams } from '@lib/utils/cache';

export interface UseCachedApiCallResult<TData> {
    response: TData | undefined;
    isLoading: boolean;
    error: string | undefined;
    requestApiCall: () => void;
}

/**
 * Drop-in replacement for useApiCall for service reads served by a cache
 * store (createCacheStore). Keeps the same return shape —
 * { response, isLoading, error, requestApiCall } — so consumer hooks
 * migrate by swapping the call itself:
 *
 *     useApiCall(EmployeeService.getEmployees, [departmentId])
 *     useCachedApiCall(employeesCache, [departmentId])
 *
 * Behavior:
 * - fresh cache entry: response available synchronously, no fetch;
 * - stale entry: response available synchronously, revalidated in the
 *   background (isLoading stays false);
 * - miss: fetches, isLoading true until first data;
 * - required params missing: no fetch, response undefined;
 * - shouldCallInitially=false: subscribes without fetching (a cached
 *   entry still renders immediately).
 *
 * requestApiCall maps to the store's refresh (always bypasses the ttl
 * window) but, exactly like useApiCall's isApiCallRequired, it is
 * deferred through state: the flag is set now, the refresh runs in an
 * effect after the current commit. This mirrors useApiCall's behavior —
 * a batched, idempotent request instead of a synchronous store write —
 * so legacy consumers whose effects re-run with unstable callbacks
 * (e.g. inline onClose arrows) cannot re-trigger the refresh mid-flush.
 */
const useCachedApiCall = <TParams extends unknown[], TData>(
    cacheStore: CacheStoreApi<TParams, TData>,
    allParams: OptionalParams<TParams>,
    requiredParams: Partial<OptionalParams<TParams>> = allParams,
    shouldCallInitially = true,
): UseCachedApiCallResult<TData> => {
    const entry = cacheStore.useEntry(...allParams);

    const hasAllRequiredParams = requiredParams.every(
        (param) => param !== undefined,
    );

    /*
     * A miss that will be fetched on mount should look loading from the
     * first paint (mirrors useApiCall's initial state), not one paint
     * later when the effect runs.
     */
    const isInitialFetchPending =
        shouldCallInitially &&
        hasAllRequiredParams &&
        cacheStore.getEntry(...allParams) === undefined;

    const [isRefreshRequired, setIsRefreshRequired] = useState(false);

    useEffect(() => {
        if (!hasAllRequiredParams || !shouldCallInitially) {
            return;
        }

        cacheStore.getData(...(allParams as TParams));
    }, [
        cacheStore,
        hasAllRequiredParams,
        shouldCallInitially,
        // Parameter arrays are stringified to avoid unnecessary API calls
        JSON.stringify(allParams),
    ]);

    useEffect(() => {
        if (!isRefreshRequired || !hasAllRequiredParams) {
            return;
        }

        setIsRefreshRequired(false);
        cacheStore.refresh(...(allParams as TParams));
    }, [
        isRefreshRequired,
        cacheStore,
        hasAllRequiredParams,
        // Parameter arrays are stringified to avoid unnecessary API calls
        JSON.stringify(allParams),
    ]);

    const requestApiCall = useCallback(() => {
        if (!hasAllRequiredParams) {
            return;
        }

        // Same-value setStates bail out, so repeated requests are idempotent.
        setIsRefreshRequired(true);
    }, [hasAllRequiredParams]);

    return {
        response: entry.data,
        isLoading: entry.isLoading || isInitialFetchPending,
        error: entry.error,
        requestApiCall,
    };
};

export default useCachedApiCall;
